import React from 'react';

import {
    useDebriefDispatch,
    useDebriefRouter,
    useDebriefSelector,
} from 'ff/debrief/hooks';
import {
    tracklogApi,
    useDeleteSegmentVrefOverrideMutation,
    useGetDebriefByTrackUUIDQuery,
    useGetDebriefSettingQuery,
    useGetDebriefStateQuery,
    useSaveDebriefSettingMutation,
    useTriggerDebriefForTrackUUIDMutation,
    useUpdateApproachOverrideMutation,
    useUpdateSegmentVrefOverrideMutation,
} from 'ff/debrief/features/TracklogApi';
import { ApproachOverride, SegmentVrefOverride } from 'ff/debrief/models';
import { findSegmentByLabelAndOffset } from 'ff/debrief/utils';
import * as AppStore from 'ff/debrief/features/AppSlice';

export function useCurrentDebrief() {
    const dispatch = useDebriefDispatch();
    const selectedSegment = useDebriefSelector(s => s.appSlice.selectedSegment);
    const [isPolling, setIsPolling] = React.useState(false);

    const router = useDebriefRouter();

    const selectedTracklogId = router.routeParams.tracklogId;

    const {
        data: debrief,
        currentData: currentDebrief,
        refetch: refetchDebriefData,
        ...debriefQueryStatus
    } = useGetDebriefByTrackUUIDQuery(selectedTracklogId, {
        skip: !selectedTracklogId,
    });

    const {
        currentData: debriefState,
        error: debriefStateError,
        ...debriefStateStatus
    } = useGetDebriefStateQuery(selectedTracklogId, {
        skip: !selectedTracklogId,
        pollingInterval: isPolling ? 1000 : undefined,
    });

    const { data: savedSettings } = useGetDebriefSettingQuery();
    const [saveDebriefSettings] = useSaveDebriefSettingMutation();

    const [triggerDebriefForTrackId] = useTriggerDebriefForTrackUUIDMutation();
    const [updateSegmentVrefOverride] = useUpdateSegmentVrefOverrideMutation();
    const [deleteSegmentVrefOverride] = useDeleteSegmentVrefOverrideMutation();
    const [updateApproachOverride] = useUpdateApproachOverrideMutation();

    const saveVrefOverride = (override: SegmentVrefOverride) => {
        updateSegmentVrefOverride({
            tracklogId: selectedTracklogId,
            override: override,
        });
        triggerWatchDebrief();
    };

    const resetVrefOverride = (override: SegmentVrefOverride) => {
        deleteSegmentVrefOverride({
            tracklogId: selectedTracklogId,
            override: override,
        });
        triggerWatchDebrief();
    };

    const saveApproachOverride = (override: ApproachOverride) => {
        updateApproachOverride({
            tracklogId: selectedTracklogId,
            override: override,
        });
        triggerWatchDebrief();
    };

    const triggerDebrief = () => {
        triggerDebriefForTrackId(selectedTracklogId);
        setIsPolling(true);
    };

    const triggerWatchDebrief = () => {
        setIsPolling(true);
    };

    React.useEffect(() => {
        const status = debriefState?.status;

        const terminalStatuses = ['COMPLETED', 'SUCCESS', 'ERROR', 'SKIPPED'];
        const processingStatuses = ['PROCESSING', 'PENDING'];

        if (terminalStatuses.includes(status)) {
            if (isPolling && (status === 'COMPLETED' || status === 'SUCCESS')) {
                dispatch(
                    tracklogApi.util.invalidateTags([
                        { type: 'Debrief', id: selectedTracklogId },
                    ])
                );

                refetchDebriefData().then(({ data }) => {
                    if (data?.debriefSegments && selectedSegment) {
                        const foundSegment = findSegmentByLabelAndOffset(
                            data.debriefSegments,
                            selectedSegment.label,
                            selectedSegment.startOffsetInSeconds
                        );

                        dispatch(
                            AppStore.actions.setSelectedSegment(foundSegment)
                        );
                    }
                });
            }

            setIsPolling(false);
        } else if (processingStatuses.includes(status)) {
            setIsPolling(true);
        } else {
            setIsPolling(false);
        }
    }, [debriefState?.status]);

    return {
        debrief: debrief,
        currentDebrief: currentDebrief,
        isScoreModeEnabled:
            savedSettings?.savedDebriefSettings.isScoreModeEnabled ?? true,
        queryStatus: debriefQueryStatus,
        hasDebrief: debriefQueryStatus.isSuccess,
        savedDebriefSettings: savedSettings,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        isUninitialized: (debriefStateError as any)?.status === 404,
        isLoading: debriefQueryStatus.isLoading || debriefStateStatus.isLoading,
        isPending: debriefState?.status === 'PENDING',
        isProcessing: debriefState?.status === 'PROCESSING',
        isSkipped: debriefState?.status === 'SKIPPED',
        isCompleted: debriefState?.status === 'COMPLETED',
        isError: debriefState?.status === 'ERROR',
        triggerDebrief,
        triggerWatchDebrief,
        saveVrefOverride,
        resetVrefOverride,
        saveApproachOverride,
        saveDebriefSettings,
    };
}
